import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Calendar, Trophy, Heart, Settings, User, LogOut, Shield, Users, Receipt, Brain, Bot, LayoutDashboard, Zap, TrendingUp, Globe, Newspaper, Flag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ThemeToggle } from './ThemeToggle';
import { useAuth } from '../context/AuthContext';
import { useBetSlip } from '../context/BetSlipContext';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Header = () => {
    const { user, isAuthenticated, logout } = useAuth();
    const { selections, toggleOpen } = useBetSlip();
    const location = useLocation();
    
    // Site settings state
    const [siteSettings, setSiteSettings] = useState({
        feature_toggles: {
            home_page: true,
            races_page: true,
            my_stable: true,
            experts_vs_ai: true,
            predictions: true,
            learning_centre: true,
            racing_hub: true
        }
    });

    // Fetch public site settings on mount
    useEffect(() => {
        const fetchSiteSettings = async () => {
            try {
                const res = await axios.get(`${API_URL}/api/site-settings/public`);
                setSiteSettings(res.data);
            } catch (err) {
                console.error('Failed to fetch site settings:', err);
                // Keep default settings if fetch fails
            }
        };
        fetchSiteSettings();
    }, []);

    // Build nav items based on site settings
    const navItems = useMemo(() => {
        const items = [];

        // Conditionally add Home if enabled
        if (siteSettings.feature_toggles?.home_page !== false) {
            items.push({ path: '/', label: 'Home', icon: Home, always: true });
        }

        // Conditionally add Races if enabled
        if (siteSettings.feature_toggles?.races_page !== false) {
            items.push({ path: '/races', label: 'Races', icon: Calendar, always: true });
        }

        // Racing Hub (racing news + today's races)
        if (siteSettings.feature_toggles?.racing_hub !== false) {
            items.push({ path: '/racing-hub', label: 'Racing Hub', icon: Flag });
        }

        // Conditionally show Competitions (horse racing tipping)
        if (siteSettings.feature_toggles?.competitions !== false) {
            items.push({ path: '/competitions', label: 'Competitions', icon: Trophy, always: true });
        }

        // Football Hub (news, scores, tables)
        if (siteSettings.feature_toggles?.football_hub !== false) {
            items.push({ path: '/football-hub', label: 'Football Hub', icon: Newspaper });
        }

        // Football Comps (score prediction games)
        if (siteSettings.feature_toggles?.football_comps !== false) {
            items.push({ path: '/football', label: 'Football Comps', icon: Globe });
        }
        
        // Conditionally add Predictions if enabled
        if (siteSettings.feature_toggles?.predictions !== false) {
            items.push({ path: '/predictions', label: 'Predictions', icon: Zap });
        }
        
        // Conditionally add Podium vs AI if enabled
        if (siteSettings.feature_toggles?.experts_vs_ai !== false) {
            items.push({ path: '/experts-vs-ai', label: 'Podium vs AI', icon: Brain });
        }
        
        // Conditionally add My Stable if enabled
        if (siteSettings.feature_toggles?.my_stable !== false) {
            items.push({ path: '/stable', label: 'My Stable', icon: Heart });
        }
        
        return items;
    }, [siteSettings]);

    const logoPath = user?.festival_guest
        ? `/competitions/${user.restricted_comp_id}`
        : (siteSettings.default_page || navItems[0]?.path || '/');

    // Festival guests get a stripped-down header — logo + sign out only,
    // no route into the rest of the app they're not allowed to see.
    if (user?.festival_guest) {
        return (
            <header className="sticky top-0 z-50 podium-header">
                <div className="container-app">
                    <div className="flex items-center justify-between gap-3 h-16">
                        <Link to={logoPath} className="flex items-center gap-3 flex-shrink-0" data-testid="logo">
                            <div className="bg-[#000028] p-1.5 rounded dark:bg-transparent flex-shrink-0">
                                <img
                                    src="https://podiumsports.com/wp-content/uploads/2025/01/Poduim-Logo.png"
                                    alt="Podium"
                                    className="h-6 w-auto max-w-none"
                                />
                            </div>
                            <div className="h-6 w-px bg-border" />
                            <span className="font-heading font-bold text-lg whitespace-nowrap">RaceWise</span>
                        </Link>
                        <div className="flex items-center gap-2 flex-shrink-0">
                            <ThemeToggle />
                            <Button variant="ghost" onClick={logout} className="gap-2" data-testid="logout-btn">
                                <LogOut className="h-4 w-4" />
                                Sign out
                            </Button>
                        </div>
                    </div>
                </div>
            </header>
        );
    }

    return (
        <header className="sticky top-0 z-50 podium-header">
            <div className="container-app">
                <div className="flex items-center justify-between gap-3 h-16">
                    {/* Podium Logo — flex-shrink-0 so a crowded tab row can never crush it */}
                    <Link to={logoPath} className="flex items-center gap-3 flex-shrink-0" data-testid="logo">
                        <div className="bg-[#000028] p-1.5 rounded dark:bg-transparent flex-shrink-0">
                            <img
                                src="https://podiumsports.com/wp-content/uploads/2025/01/Poduim-Logo.png"
                                alt="Podium"
                                className="h-6 w-auto max-w-none"
                            />
                        </div>
                        <div className="h-6 w-px bg-border" />
                        <span className="font-heading font-bold text-lg whitespace-nowrap">RaceWise</span>
                    </Link>

                    {/* Desktop Nav — the shrinking element: scrolls when tabs overflow */}
                    <nav className="hidden md:flex items-center gap-1 min-w-0 overflow-x-auto">
                        {navItems.map(item => (
                            <Link key={item.path} to={item.path} className="flex-shrink-0">
                                <Button
                                    variant={location.pathname === item.path ? 'secondary' : 'ghost'}
                                    className="gap-2 whitespace-nowrap"
                                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                    <item.icon className="h-4 w-4" />
                                    {item.label}
                                </Button>
                            </Link>
                        ))}
                    </nav>

                    {/* Right side */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {/* Bet Slip Button */}
                        {isAuthenticated && (
                            <Button
                                variant="outline"
                                className="relative gap-2"
                                onClick={toggleOpen}
                                data-testid="betslip-toggle"
                            >
                                <span className="font-data font-semibold">Bet Slip</span>
                                {selections.length > 0 && (
                                    <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground w-5 h-5 rounded-full text-xs flex items-center justify-center font-semibold">
                                        {selections.length}
                                    </span>
                                )}
                            </Button>
                        )}

                        <ThemeToggle />

                        {/* Auth */}
                        {isAuthenticated ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="relative h-9 w-9 rounded-full" data-testid="user-menu">
                                        <Avatar className="h-9 w-9">
                                            <AvatarFallback className="bg-primary text-primary-foreground font-semibold">
                                                {user?.name?.charAt(0).toUpperCase() || 'U'}
                                            </AvatarFallback>
                                        </Avatar>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="w-56" align="end">
                                    <div className="px-2 py-1.5">
                                        <p className="text-sm font-medium">{user?.name}</p>
                                        <p className="text-xs text-muted-foreground">{user?.email}</p>
                                    </div>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem asChild>
                                        <Link to="/settings" className="cursor-pointer" data-testid="settings-link">
                                            <Settings className="mr-2 h-4 w-4" />
                                            Settings
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to="/bets" className="cursor-pointer" data-testid="bet-history-link">
                                            <Receipt className="mr-2 h-4 w-4" />
                                            Bet History
                                        </Link>
                                    </DropdownMenuItem>
                                    {user?.is_admin && (
                                        <>
                                            <DropdownMenuSeparator />
                                            <div className="px-2 py-1.5">
                                                <p className="text-xs text-muted-foreground font-semibold">Admin</p>
                                            </div>
                                            <DropdownMenuItem asChild>
                                                <Link to="/admin/dashboard" className="cursor-pointer" data-testid="admin-dashboard-link">
                                                    <LayoutDashboard className="mr-2 h-4 w-4" />
                                                    Site Dashboard
                                                </Link>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem asChild>
                                                <Link to="/admin/competitions" className="cursor-pointer" data-testid="admin-competitions-link">
                                                    <Trophy className="mr-2 h-4 w-4" />
                                                    Competitions
                                                </Link>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem asChild>
                                                <Link to="/admin/football" className="cursor-pointer" data-testid="admin-football-link">
                                                    <Globe className="mr-2 h-4 w-4" />
                                                    Football Comps
                                                </Link>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem asChild>
                                                <Link to="/admin/users" className="cursor-pointer" data-testid="admin-users-link">
                                                    <Users className="mr-2 h-4 w-4" />
                                                    User Management
                                                </Link>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem asChild>
                                                <Link to="/admin/ai-settings" className="cursor-pointer" data-testid="admin-ai-link">
                                                    <Brain className="mr-2 h-4 w-4" />
                                                    AI Settings
                                                </Link>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem asChild>
                                                <Link to="/admin/predictions" className="cursor-pointer" data-testid="admin-predictions-link">
                                                    <Zap className="mr-2 h-4 w-4" />
                                                    Predictions
                                                </Link>
                                            </DropdownMenuItem>
                                            <DropdownMenuItem asChild>
                                                <Link to="/admin/engagement" className="cursor-pointer" data-testid="admin-engagement-link">
                                                    <TrendingUp className="mr-2 h-4 w-4" />
                                                    Engagement
                                                </Link>
                                            </DropdownMenuItem>
                                        </>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={logout} className="cursor-pointer text-destructive" data-testid="logout-btn">
                                        <LogOut className="mr-2 h-4 w-4" />
                                        Log out
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <Link to="/login">
                                <Button className="btn-pill" data-testid="login-btn">
                                    Sign In
                                </Button>
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};

export const MobileNav = () => {
    const location = useLocation();
    const { isAuthenticated, user } = useAuth();

    // Site settings state
    const [siteSettings, setSiteSettings] = useState({
        feature_toggles: {
            home_page: true,
            races_page: true,
            my_stable: true,
            experts_vs_ai: true,
            predictions: true,
            learning_centre: true,
            racing_hub: true
        }
    });

    // Fetch public site settings on mount
    useEffect(() => {
        const fetchSiteSettings = async () => {
            try {
                const res = await axios.get(`${API_URL}/api/site-settings/public`);
                setSiteSettings(res.data);
            } catch (err) {
                console.error('Failed to fetch site settings:', err);
            }
        };
        fetchSiteSettings();
    }, []);

    // Build nav items based on site settings
    const navItems = useMemo(() => {
        const items = [];

        // Conditionally add Home if enabled
        if (siteSettings.feature_toggles?.home_page !== false) {
            items.push({ path: '/', label: 'Home', icon: Home });
        }

        // Conditionally add Races if enabled
        if (siteSettings.feature_toggles?.races_page !== false) {
            items.push({ path: '/races', label: 'Races', icon: Calendar });
        }

        // Racing Hub
        if (siteSettings.feature_toggles?.racing_hub !== false) {
            items.push({ path: '/racing-hub', label: 'Racing', icon: Flag });
        }

        // Conditionally show Competitions/Tips
        if (siteSettings.feature_toggles?.competitions !== false) {
            items.push({ path: '/competitions', label: 'Tips', icon: Trophy });
        }

        // Football Hub
        if (siteSettings.feature_toggles?.football_hub !== false) {
            items.push({ path: '/football-hub', label: 'Hub', icon: Newspaper });
        }

        // Football Comps
        if (siteSettings.feature_toggles?.football_comps !== false) {
            items.push({ path: '/football', label: 'Football', icon: Globe });
        }
        
        // Conditionally add Predictions if enabled
        if (siteSettings.feature_toggles?.predictions !== false) {
            items.push({ path: '/predictions', label: 'Predict', icon: Zap });
        }
        
        // Conditionally add My Stable if enabled (removed Podium vs AI from mobile for space)
        if (siteSettings.feature_toggles?.my_stable !== false) {
            items.push({ path: '/stable', label: 'Stable', icon: Heart });
        }
        
        return items;
    }, [siteSettings]);

    // Festival guests are locked to a single page — nothing to nav to
    if (user?.festival_guest) {
        return null;
    }

    return (
        <nav className="mobile-nav md:hidden">
            <div className="flex items-center justify-around py-2">
                {navItems.map(item => (
                    <Link
                        key={item.path}
                        to={item.path}
                        className={`flex flex-col items-center gap-1 px-3 py-1 rounded-lg transition-colors ${
                            location.pathname === item.path 
                                ? 'text-primary' 
                                : 'text-muted-foreground hover:text-foreground'
                        }`}
                        data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                        <item.icon className="h-5 w-5" />
                        <span className="text-xs font-medium">{item.label}</span>
                    </Link>
                ))}
            </div>
        </nav>
    );
};
